import { BaseElement, defineElement, html } from '@webtides/element-js';

class ResponsiveTableController extends BaseElement {
    #resizeInterval;
    #resized;

    properties() {
        return {
            tabSelector: 'th',
            headerSelector: 'thead',
            rowSelector: 'tbody tr',
            cellSelector: 'td',
            collapsedCellClass: 'hidden',
            collapsedHeaderClass: 'block',
            collapsedTableClass: 'block',
            activeTabClass: 'active',
            activeIndex: 0,
            collapsed: false,
        };
    }

    shouldCollapseTable() {
        if (this.collapsed) {
            return true;
        }

        const table = this.children[0];

        if (!table) {
            return false;
        }

        const { scrollWidth } = table;

        return scrollWidth > this.offsetWidth;
    }

    setCellsHidden(none = false) {
        const rows = this.querySelectorAll(this.rowSelector);
        rows.forEach(row => {
            const cells = row.querySelectorAll(this.cellSelector);
            cells.forEach((cell, index) => {
                if (none) {
                    cell.classList.remove(this.collapsedCellClass);
                    return;
                }

                cell.classList.toggle(this.collapsedCellClass, index !== this.activeIndex);
            });
        });
    }

    toggleCollapse() {
        this.setCellsHidden(!this.collapsed);
        this.querySelector(this.headerSelector).classList.toggle(this.collapsedHeaderClass, this.collapsed);
        this.children[0]?.classList.toggle(this.collapsedTableClass, this.collapsed);
    }

    connected() {
        requestAnimationFrame(() => {
            this.collapsed = this.shouldCollapseTable();
            this.toggleCollapse();

            const tabs = this.querySelectorAll(this.tabSelector);
            tabs[this.activeIndex]?.classList.add(this.activeTabClass);
        });

        this.#resizeInterval = setInterval(() => {
            if (this.#resized) {
                this.collapsed = this.shouldCollapseTable();
                this.#resized = false;
            }
        }, 500);
    }
    disconnected() {
        clearInterval(this.#resizeInterval);
    }

    watch() {
        return {
            activeIndex: () => {
                this.setCellsHidden();
            },
            collapsed: () => {
                this.toggleCollapse();
            },
        };
    }

    events() {
        return {
            window: {
                resize: () => {
                    this.#resized = true;
                },
            },
            [this.tabSelector]: {
                click: ev => {
                    if (!this.collapsed) {
                        return;
                    }

                    const parent = ev.currentTarget.parentElement;
                    const children = Array.from(parent.querySelectorAll(this.tabSelector));
                    this.activeIndex = children.findIndex(child => child === ev.currentTarget) ?? 0;

                    children.forEach((child, index) =>
                        child.classList.toggle(this.activeTabClass, index === this.activeIndex),
                    );
                },
            },
        };
    }
}

defineElement('responsive-table-controller', ResponsiveTableController);
